import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { usePwaGlobalSearch } from '../hooks/usePwaGlobalSearch';
import { openProjectFromOpportunity } from '../utils/projectNavigation';
import './PwaGlobalSearch.css';

const GLOBAL_SEARCH_OPEN_CLASS = 'nettles-pwa-global-search-open';

function PwaGlobalSearch({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { authFetch, isReady } = useAuthFetch();
  const inputRef = useRef(null);
  const isSelectingRef = useRef(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const {
    query,
    setQuery,
    clearQuery,
    results,
    isLoading,
    loadError,
    showMinCharsHint,
    showNoResults,
    hasProjects,
    minSearchLength,
  } = usePwaGlobalSearch(isOpen);

  useEffect(() => {
    if (!isOpen) {
      document.documentElement.classList.remove(GLOBAL_SEARCH_OPEN_CLASS);
      return undefined;
    }

    document.documentElement.classList.add(GLOBAL_SEARCH_OPEN_CLASS);
    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.documentElement.classList.remove(GLOBAL_SEARCH_OPEN_CLASS);
    };
  }, [isOpen]);

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSelect = useCallback(async (item) => {
    if (!isReady || isSelectingRef.current) {
      return;
    }

    isSelectingRef.current = true;
    setIsSelecting(true);
    try {
      await openProjectFromOpportunity({
        authFetch,
        navigate,
        opportunityId: item.opportunityId,
        projectRecord: item.project,
      });
      onClose();
    } catch (error) {
      console.error('Error opening project from global search:', error);
    } finally {
      isSelectingRef.current = false;
      setIsSelecting(false);
    }
  }, [authFetch, isReady, navigate, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="pwa-global-search" role="dialog" aria-modal="true" aria-label="Search projects">
      <button
        type="button"
        className="pwa-global-search__backdrop"
        aria-label="Close search"
        onClick={handleBackdropClick}
      />
      <div className="pwa-global-search__panel">
        <div className="pwa-global-search__header">
          <div className="pwa-global-search__input-wrap">
            <FiSearch className="pwa-global-search__search-icon" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              className="pwa-global-search__input"
              placeholder="Search projects"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {query.length > 0 && (
              <button
                type="button"
                className="pwa-global-search__clear"
                aria-label="Clear search"
                onClick={clearQuery}
              >
                <FiX aria-hidden="true" />
              </button>
            )}
          </div>
          <button
            type="button"
            className="pwa-global-search__cancel"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>

        <div className="pwa-global-search__body">
          {loadError && !hasProjects && (
            <p className="pwa-global-search__message pwa-global-search__message--error">
              {loadError}
            </p>
          )}

          {!loadError && isLoading && !hasProjects && (
            <p className="pwa-global-search__message">Loading projects...</p>
          )}

          {!loadError && showMinCharsHint && (
            <p className="pwa-global-search__message">
              Type at least {minSearchLength} characters to search.
            </p>
          )}

          {!loadError && showNoResults && (
            <p className="pwa-global-search__message">No projects found.</p>
          )}

          {results.length > 0 && (
            <section className="pwa-global-search__section">
              <h2 className="pwa-global-search__section-title">Projects</h2>
              <ul className="pwa-global-search__results">
                {results.map((item) => (
                  <li key={item.opportunityId}>
                    <button
                      type="button"
                      className="pwa-global-search__result"
                      disabled={isSelecting}
                      onClick={() => handleSelect(item)}
                    >
                      <span className="pwa-global-search__result-name">
                        {item.projectName || 'Untitled project'}
                      </span>
                      <span className="pwa-global-search__result-meta">
                        {item.projectNo ? `PID: ${item.projectNo}` : 'PID: —'}
                        {item.opportunityNo ? ` · OID: ${item.opportunityNo}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PwaGlobalSearch;
